import { NIVEAUX_FLUENCE } from './fluence-reperes';

export interface PointEcole {
  readonly studentId: string;
  readonly levelCode: string;
  readonly eleveActif: boolean;
  readonly completedAt: string;
  readonly position: 'sous' | 'dans' | 'au-dessus' | null;
}

export interface LigneNiveau {
  readonly niveau: string;
  readonly mesures: number;
  readonly sous: number;
  readonly dans: number;
  readonly au: number;
  readonly recentes: number;
}

export interface PointEcoleClasse extends PointEcole {
  readonly classroomId: string;
  readonly classroomNom: string;
  readonly identiteEleveId: string;
  readonly classroomActuelId: string | null;
  readonly classroomActuelNom: string | null;
}

export interface LigneClasse {
  readonly classroomId: string;
  readonly classroomNom: string;
  readonly eleves: number;
  readonly sous: number;
  readonly dans: number;
  readonly au: number;
  readonly mesures: number;
  readonly recentes: number;
  readonly lecturesHeritees: number;
}

/* La SOURCE est `fluence-reperes` — une liste, un seul endroit. Réexportée ici
   pour ne pas casser les appelants qui la lisaient déjà d'ici. */
export const NIVEAUX = NIVEAUX_FLUENCE;
export const JOURS_RECENT = 30;

/* La DERNIÈRE lecture de chaque élève. Les points arrivent du plus récent au
   plus ancien, donc la première rencontrée est la bonne. */
export const dernieresLectures = <T extends { readonly studentId: string }>(
  points: readonly T[],
): readonly T[] => {
  const vu = new Set<string>();
  const sortie: T[] = [];
  for (const p of points) {
    if (vu.has(p.studentId)) continue;
    vu.add(p.studentId);
    sortie.push(p);
  }
  return sortie;
};

/* Extrait de l'écran pour être PROUVABLE. Tant qu'il vivait dans un `useMemo`,
   le seul moyen de vérifier ce décompte était de le relire — et une relecture
   avait déjà laissé passer le double comptage des lectures d'un même enfant. */
export function lignesParNiveau(
  points: readonly PointEcole[],
  maintenant: number = Date.now(),
): readonly LigneNiveau[] {
  const limite = maintenant - JOURS_RECENT * 86400000;

  return NIVEAUX.map((niveau) => {
    const p = points.filter((x) => x.levelCode === niveau);

    /* Ces trois colonnes comptent des ÉLÈVES ENCORE INSCRITS, sur leur dernière
       lecture. Deux corrections y sont nécessaires, pas une :

       — la DÉDUPLICATION, parce qu'un enfant mesuré trois fois sous le repère
         comptait pour trois, et qu'un directeur lisant « 12 » croyait douze
         enfants là où il pouvait n'y en avoir quatre ;

       — le filtre des élèves PARTIS, parce que la liste « ouvrir un élève »,
         juste en dessous, les exclut déjà. Sans lui, le même écran affiche deux
         chiffres pour la même chose : un effectif qui compte des enfants que le
         directeur ne trouvera nulle part en dessous. Aucun élève n'est inactif
         en base aujourd'hui — c'est une dérive qu'on ferme, pas un défaut
         qu'on répare. */
    const derniere = dernieresLectures(p).filter((x) => x.eleveActif);

    return {
      niveau,
      /* Celles-ci restent des comptes de LECTURES — une activité de mesure, pas
         un état présent. Une lecture faite par un enfant depuis parti a bien eu
         lieu, et l'en-tête du tableau le dit. */
      mesures: p.length,
      sous: derniere.filter((x) => x.position === 'sous').length,
      dans: derniere.filter((x) => x.position === 'dans').length,
      au: derniere.filter((x) => x.position === 'au-dessus').length,
      recentes: p.filter((x) => x.completedAt && Date.parse(x.completedAt) >= limite).length,
    };
  });
}

/* Vue HYBRIDE par classe. Deux questions différentes, deux ancrages :

   — l'ÉTAT PRÉSENT (élèves, sous / dans / au-delà) suit la classe ACTUELLE de
     l'élève, sur sa dernière lecture quelle que soit la classe où elle a été
     prise : une classe créée après une scission apparaît dès qu'un de ses
     élèves a une mesure, sans attendre qu'on y en prenne une nouvelle ;

   — l'HISTORIQUE (lectures, récentes) reste ancré sur la classe au moment de la
     mesure, pour que la tendance d'une classe ne soit pas réécrite par les
     mouvements d'élèves.

   Un enfant se compte par IDENTITÉ, pas par inscription : changer de classe ne
   le double pas. `lecturesHeritees` dit combien des lectures de ses élèves
   actuels viennent d'une autre classe — c'est ce qui explique une classe neuve
   qui a des élèves mesurés mais aucune lecture à son nom. */
export function lignesParClasse(
  points: readonly PointEcoleClasse[],
  maintenant: number = Date.now(),
): readonly LigneClasse[] {
  const limite = maintenant - JOURS_RECENT * 86400000;

  const noms = new Map<string, string>();
  for (const p of points) {
    noms.set(p.classroomId, p.classroomNom);
    if (p.classroomActuelId && p.classroomActuelNom) noms.set(p.classroomActuelId, p.classroomActuelNom);
  }

  const derniereParIdentite = new Map<string, PointEcoleClasse>();
  for (const p of points) {
    if (!derniereParIdentite.has(p.identiteEleveId)) derniereParIdentite.set(p.identiteEleveId, p);
  }

  return [...noms.entries()]
    .map(([classroomId, classroomNom]): LigneClasse => {
      const presents = [...derniereParIdentite.values()].filter((x) => x.classroomActuelId === classroomId);
      const identitesPresentes = new Set(presents.map((x) => x.identiteEleveId));
      const lectures = points.filter((x) => x.classroomId === classroomId);
      return {
        classroomId,
        classroomNom,
        eleves: presents.length,
        sous: presents.filter((x) => x.position === 'sous').length,
        dans: presents.filter((x) => x.position === 'dans').length,
        au: presents.filter((x) => x.position === 'au-dessus').length,
        mesures: lectures.length,
        recentes: lectures.filter((x) => x.completedAt && Date.parse(x.completedAt) >= limite).length,
        lecturesHeritees: points.filter(
          (x) => identitesPresentes.has(x.identiteEleveId) && x.classroomId !== classroomId,
        ).length,
      };
    })
    .sort((a, b) => a.classroomNom.localeCompare(b.classroomNom, 'fr'));
}
